/**
 * PayPageHeader â€” clinic + patient strip at the top of the pay page.
 *
 * Mirrors the screenshot layout: clinic identity on the left, patient
 * identity on the right with address and MRN (DOB is intentionally NOT
 * displayed â€” patients recognize their account by name and address, and
 * DOB on screen is unnecessary PHI exposure per QA feedback v0.7.2).
 * On mobile, the two
 * stack vertically.
 *
 * Branding decision (scoping #5): show the *clinic name* prominently
 * because that's what the patient recognizes. The Iris/Trillium/Tensaw
 * platform vendor name is intentionally absent.
 */
import { Stethoscope } from 'lucide-react';

import { Avatar } from '@tensaw/design-system/primitives';
import { Card, CardContent } from '@tensaw/design-system/layout';

import type { ClinicInfo, PatientInfo } from '../../api/types';

interface PayPageHeaderProps {
  clinic: ClinicInfo;
  patient: PatientInfo;
}

export function PayPageHeader({ clinic, patient }: PayPageHeaderProps) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-6 p-6 sm:flex-row sm:items-start sm:justify-between sm:gap-10">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Stethoscope className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="text-base font-semibold leading-tight text-foreground">
              {clinic.name ?? 'Your clinic'}
            </div>
            <div className="mt-1 whitespace-pre-line text-xs leading-relaxed text-muted-foreground">
              {/*
                v0.8.5: `whitespace-pre-line` is required for the address
                helper's `\n` line separators to render as actual line
                breaks. Without it, the address jams onto one line and
                truncates mid-zip on narrow widths. The patient block
                already has this; clinic block was missing it (caught
                in QA today).
              */}
              {formatAddress(clinic)}
            </div>
            {clinic.phone && (
              <div className="mt-1 text-xs text-muted-foreground">
                {/*
                  Clinic-identity strip uses the storefront-style
                  `clinic.phone` (main number), NOT `billing_phone`. The
                  surrounding chrome reads "this is the clinic, here's
                  who they are" â€” a "Phone: $billing_inquiries_line"
                  here would be misleading. Billing-context renders
                  (footer, Stripe section, success page) use
                  `pickContactPhone()`.
                */}
                Phone: {formatPhone(clinic.phone)}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-start gap-3 sm:justify-end">
          <Avatar
            alt={patient.name}
            fallbackText={initialsOf(patient.name)}
            size="lg"
          />
          <div className="min-w-0">
            <div className="text-sm font-medium text-foreground">{patient.name}</div>
            {(patient.address_line1 || patient.city) && (
              <div className="mt-1 whitespace-pre-line text-[12px] leading-relaxed text-muted-foreground">
                {formatAddress(patient)}
              </div>
            )}
            {/*
              MRN as a faint trailing line. Some clinics tell patients to
              reference their MRN on calls, so it stays â€” but
              de-emphasized to monospace + faint color so it doesn't
              compete with the name. Note DOB is deliberately omitted
              per QA v0.7.2 feedback (unnecessary PHI on screen; the
              patient already authenticated with it).
            */}
            {patient.mrn && (
              <div className="mt-1 font-mono text-[12px] text-muted-foreground/70 tabular-nums">
                MRN {patient.mrn}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0]?.[0] ?? '?').toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase();
}

function formatAddress(
  a: Pick<PatientInfo, 'address_line1' | 'address_line2' | 'city' | 'state' | 'zip'>,
): string {
  // US postal-style: comma between city and state, space before zip.
  // Lines joined with `\n` (rendered via `whitespace-pre-line` at the
  // call site). Falsy fields drop out cleanly without leaving dangling
  // separators.
  const lines: string[] = [];
  if (a.address_line1) lines.push(a.address_line1);
  if (a.address_line2) lines.push(a.address_line2);
  const cityState =
    a.city && a.state
      ? `${a.city}, ${a.state}`
      : a.city ?? a.state ?? '';
  const cityStateZip = [cityState, a.zip].filter(Boolean).join(' ').trim();
  if (cityStateZip) lines.push(cityStateZip);
  return lines.join('\n');
}

function formatPhone(p: string): string {
  const digits = p.replace(/\D/g, '');
  if (digits.length !== 10) return p;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}
